import { useState, useEffect } from 'react';

import './Units.css';

import { GetDataPage, RegisterDataPage, UnRegisterDataPage } from '../../interface/Data';
import { SetStatusGlobal } from '../../interface/Global';

import Comp_Select from '../../components/Select';
import type { SelectItem, SelectOption } from '../../components/Select';

import { AnalyticsApartment, GetDataUnits, IsNotSet } from './Analytics';
import { ProjectNamesByCompany, ReportClientMap, ReportProjectMap, ShowClientList, SortOptions } from './Filters';

// os dados do Analytics chegam achatados e são agrupados em country > client >
// project > apartments
interface ProjectGroup {
    name       : string;
    region     : string;
    city       : string;
    apartments : AnalyticsApartment[];
}

interface ClientGroup {
    client   : string;
    projects : ProjectGroup[];
}

interface CountryGroup {
    country : string;
    clients : ClientGroup[];
}

export default function Page_Reports_Units() {

    const [ listClients, setListClients ] = useState<ReportClientMap>(GetDataPage('clients') || {});
    const [ listProjects, setListProjects ] = useState<ReportProjectMap>(GetDataPage('projects') || {});

    const [ company, setCompany ] = useState<SelectOption<number>>({ "value": 0, "label": "All clients", "qtd_project": 0 });
    const [ country, setCountry ] = useState<SelectOption<string>>({ "value": "", "label": "All countries" });
    const [ region, setRegion ] = useState<SelectOption<string>>({ "value": "", "label": "All regions" });
    const [ city, setCity ] = useState<SelectOption<string>>({ "value": "", "label": "All cities" });
    const [ project, setProject ] = useState<SelectOption<string>>({ "value": "", "label": "All projects" });

    const [ dataApartments, setDataApartments ] = useState<CountryGroup[]>([]);

    function ShowDataAnalytics(){
        SetStatusGlobal('loading', true);

        GetDataUnits()
        .then(response => {
            if (response.apartments) {
                const filtered = response.apartments.filter(item =>
                    !IsNotSet(item.country) && !IsNotSet(item.client) && !IsNotSet(item.project) && !IsNotSet(item.apartment_number)
                );

                // agrupa por country → client → project → apartments
                const countryMap: { [country: string]: { [client: string]: { [project: string]: { [apartment: string]: AnalyticsApartment } } } } = {};
                filtered.forEach(item => {
                    const { country, client, project, apartment_number } = item;

                    if (!countryMap[country]) countryMap[country] = {};
                    if (!countryMap[country][client]) countryMap[country][client] = {};
                    if (!countryMap[country][client][project]) countryMap[country][client][project] = {};

                    if (countryMap[country][client][project][apartment_number]) {
                        countryMap[country][client][project][apartment_number].event_count += item.event_count;
                    } else {
                        countryMap[country][client][project][apartment_number] = { ...item };
                    }
                });

                const grouped: CountryGroup[] = Object.entries(countryMap).map(([country, clientMap]) => ({
                    country,
                    clients: Object.entries(clientMap).map(([client, projectMap]) => ({
                        client,
                        projects: Object.entries(projectMap).map(([project, aptMap]) => {
                            const apartments = Object.values(aptMap).sort((a, b) => a.apartment_number.localeCompare(b.apartment_number));
                            return {
                                name: project,
                                region: apartments[0]?.region || '',
                                city: apartments[0]?.city || '',
                                apartments,
                            };
                        }).sort((a, b) => a.name.localeCompare(b.name)),
                    })).sort((a, b) => a.client.localeCompare(b.client)),
                })).sort((a, b) => a.country.localeCompare(b.country));

                setDataApartments(grouped);
            }
            SetStatusGlobal('loading', false);
        })
        .catch(error => {
            SetStatusGlobal('loading', false);
            console.error("Erro ao carregar units:", error);
        });
    }

    function getDataFilteredByCompany(): CountryGroup[] {
        let result = dataApartments;
        if (company.value) {
            const projectNamesForCompany = ProjectNamesByCompany(listProjects, company.value);
            if (projectNamesForCompany.length === 0) return [];
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cg => ({
                            ...cg,
                            projects: cg.projects.filter(p => projectNamesForCompany.includes(p.name.trim().toLowerCase()))
                        }))
                        .filter(cg => cg.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }
        return result;
    }

    function getAllProjects(data: CountryGroup[]): ProjectGroup[] {
        return data.flatMap(c => c.clients.flatMap(cg => cg.projects));
    }

    function ShowCountryList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All countries" }];
        getDataFilteredByCompany().forEach(item => {
            if (!newData.find(c => c.id === item.country)) {
                newData.push({ "id": item.country, "name": item.country });
            }
        });
        return SortOptions(newData);
    }

    function ShowRegionList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All regions" }];
        let base = getDataFilteredByCompany();
        if (country.value) base = base.filter(c => c.country === country.value);
        getAllProjects(base).forEach(project => {
            if (project.region && !newData.find(r => r.id === project.region)) {
                newData.push({ "id": project.region, "name": project.region });
            }
        });
        return SortOptions(newData);
    }

    function ShowCityList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All cities" }];
        let base = getDataFilteredByCompany();
        if (country.value) base = base.filter(c => c.country === country.value);
        let projects = getAllProjects(base);
        if (region.value) projects = projects.filter(p => p.region === region.value);
        projects.forEach(project => {
            if (project.city && !newData.find(ci => ci.id === project.city)) {
                newData.push({ "id": project.city, "name": project.city });
            }
        });
        return SortOptions(newData);
    }

    function ShowProjectList(): SelectItem<string>[] {
        const newData: SelectItem<string>[] = [{ "id": "", "name": "All projects" }];
        let base = getDataFilteredByCompany();
        if (country.value) base = base.filter(c => c.country === country.value);
        let projects = getAllProjects(base);
        if (region.value) projects = projects.filter(p => p.region === region.value);
        if (city.value) projects = projects.filter(p => p.city === city.value);
        projects.forEach(p => {
            if (!newData.find(item => item.id === p.name)) {
                newData.push({ "id": p.name, "name": p.name });
            }
        });
        return SortOptions(newData);
    }

    function handleSetCompany(val: SelectOption<number>){
        setCompany(val);
        setCountry({ "value": "", "label": "All countries" });
        setRegion({ "value": "", "label": "All regions" });
        setCity({ "value": "", "label": "All cities" });
        setProject({ "value": "", "label": "All projects" });
    }

    function handleSetCountry(val: SelectOption<string>){
        setCountry(val);
        setRegion({ "value": "", "label": "All regions" });
        setCity({ "value": "", "label": "All cities" });
        setProject({ "value": "", "label": "All projects" });
    }

    function handleSetRegion(val: SelectOption<string>){
        setRegion(val);
        setCity({ "value": "", "label": "All cities" });
        setProject({ "value": "", "label": "All projects" });
    }

    function handleSetCity(val: SelectOption<string>){
        setCity(val);
        setProject({ "value": "", "label": "All projects" });
    }

    function FilteredData(): CountryGroup[] {
        let result = getDataFilteredByCompany();

        if (country.value) {
            result = result.filter(c => c.country === country.value);
        }

        if (region.value) {
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cg => ({ ...cg, projects: cg.projects.filter(p => p.region === region.value) }))
                        .filter(cg => cg.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }

        if (city.value) {
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cg => ({ ...cg, projects: cg.projects.filter(p => p.city === city.value) }))
                        .filter(cg => cg.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }

        if (project.value) {
            result = result
                .map(c => ({
                    ...c,
                    clients: c.clients
                        .map(cg => ({ ...cg, projects: cg.projects.filter(p => p.name === project.value) }))
                        .filter(cg => cg.projects.length > 0)
                }))
                .filter(c => c.clients.length > 0);
        }

        return result;
    }

    useEffect(() => {
        ShowDataAnalytics();

        RegisterDataPage('clients', setListClients);
        RegisterDataPage('projects', setListProjects);
        return ()=>{
            UnRegisterDataPage('clients', setListClients);
            UnRegisterDataPage('projects', setListProjects);
        };
    }, []);

    const filteredData = FilteredData();

    return(
        <div className="page_report page_units">
            <div className="div_list_client margin_bottom">
                <Comp_Select title="Client" list={ ShowClientList(listClients) } value={ company } setValue={ handleSetCompany } required={ true } />

                <Comp_Select title="Project" list={ ShowProjectList() } value={ project } setValue={ setProject } required={ false } />
            </div>

            <div className="div_list_client">
                <Comp_Select title="Country" list={ ShowCountryList() } value={ country } setValue={ handleSetCountry } required={ false } />

                <Comp_Select title="Region" list={ ShowRegionList() } value={ region } setValue={ handleSetRegion } required={ false } />

                <Comp_Select title="City" list={ ShowCityList() } value={ city } setValue={ handleSetCity } required={ false } />
            </div>
            <div className="list_data">
                {
                    filteredData.length === 0 && (company.value || country.value || region.value || city.value || project.value) ?
                        <div className="no_data_found">No data found for the selected filters</div>
                    : null
                }
                {
                    filteredData.map((country, country_index) =>{
                        return(
                            <div className="list_country" key={ "loop_" + country_index }>
                                <div className="name_country">{ country.country }</div>
                                <div className="list_client">
                                    {
                                        country.clients.map((clientGroup, client_index) =>(
                                            <div className="data_client" key={ "client_" + client_index }>
                                                <div className="client_name">
                                                    { clientGroup.client }
                                                </div>
                                                <div className="list_units">
                                                    {
                                                        clientGroup.projects.map((project, project_index) =>{
                                                            return(
                                                                <div className="div_project" key={ "project_" + project_index }>
                                                                    <div className="data_project">
                                                                        Project: <span className="data_project_name">{ project.name }</span>
                                                                    </div>
                                                                    {/*
                                                                    <div className="data_project inf_project">
                                                                        <div className="">
                                                                            Region: { project.region }
                                                                        </div>
                                                                        <div className="">
                                                                            City: { project.city }
                                                                        </div>
                                                                    </div>
                                                                    */}
                                                                    <div className="list_apart">
                                                                        <div className="inf_access div_topic">
                                                                            <div className="topic_unit">Unit</div>
                                                                            <div className="topic_number">Qtd access</div>
                                                                        </div>
                                                                        {
                                                                            project.apartments.map((apt, apt_index) => {
                                                                                return(
                                                                                    <div className="inf_access" key={ "apt_" + apt_index }>
                                                                                        <div className="topic_unit">{ apt.apartment_number }</div>
                                                                                        <div className="topic_number">{ apt.event_count }</div>
                                                                                    </div>
                                                                                )
                                                                            })
                                                                        }
                                                                    </div>
                                                                </div>
                                                            )
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    );
}
